<template>
  <div>
    <v-row no-gutters>
      <v-col align="center">
        <p class="text-h1 my-0">{{formatTimeStringDuration(duration)}}</p>
        <span class="text-body-2 my-0">Startzeit: {{start_time_str}}</span>
        <v-tooltip bottom :disabled="!value.pauses.length">
          <template v-slot:activator="{ on, attrs }">
            <p v-bind="attrs" v-on="on" class="text-body-2 my-0">Pausen: {{formatTimeStringDuration(pauseDuration)}}</p>
          </template>
            <p v-for="pause in value.pauses" :key="pause.id" class="my-0">
              {{ formatTime(pause.start_time) }} bis {{ pause.end_time ? (formatTime(pause.end_time)) : "jetzt" }}
            </p>
          </v-tooltip>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="py-0">
        <v-text-field
            v-model="value.comment"
            type="text"
            placeholder="Bemerkung"
            class="py-0"
            clearable
            :disabled="loading || profileLoading"
            @change="onCommentChange"
          />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col align="center" class="pr-1">
        <v-btn
          class="w-100"
          @click="onStartClick"
          :disabled="loading || profileLoading || buttonState[0].disabled"
          text
          outlined
          primary
        >
          {{buttonState[0].text}}
        </v-btn>
      </v-col>
      <v-col align="center" class="pl-1">
        <v-btn
          class="w-100"
          @click="onStopClick"
          :disabled="loading || profileLoading || buttonState[1].disabled"
          text
          outlined
          primary
        >
          {{buttonState[1].text}}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import { api, endpoints } from '@/api'

import {formatTimeStringDuration} from '../../filters/Duration'
import {formatTime, formatDateTime} from '../../filters/Time'

import _ from 'lodash'

export default {
  name: 'TimeClock',
  data () {
    return {
      value: {
        id: null,
        start_time: null,
        comment: "Arbeitszeit",
        pauses: []
      },
      loading: false,
      duration: 0,
      pauseDuration: 0,
      timer: null,
    }
  },
  destroyed() {
    clearInterval(this.timer)
  },
  computed: {
    ...mapGetters([
      'profile',
      'profileLoading'
    ]),
    start_time_str () {
      if(!this.value.start_time){
        return "--:--"
      }
      else {
        return formatTime(moment(this.value.start_time))
      }
    },
    buttonState () {
      if (this.timerActive) {
        if (this.timerPaused) {
          return [
             {text: "Fortfahren", disabled: false},
             {text: "Stopp", disabled: false},
           ]
        }
        else {
          return [
             {text: "Pause", disabled: false},
             {text: "Stopp", disabled: false},
           ]
        }
      }
     return [
       {text: "Start", disabled: false},
       {text: "Stopp", disabled: true},
     ]
    },
    timerActive () {
      return this.timer !== null && this.value.id !== null
    },
    currentPause () {
      let filtered = this.value.pauses.filter(i => !i.end_time)
      if (!this.timerActive || filtered.length === 0) {
        return null
      }
      return _.orderBy(filtered, "start_time", "desc")[0]
    },
    timerPaused () {
      return this.currentPause !== null
    }
  },
  watch: {
    "value.start_time" : {
      handler () {
        if(this.timer) {
          this.updateDuration()
          clearInterval(this.timer)
        }
        if (this.value.start_time !== null) {
           this.updateDuration()
           this.timer = setInterval(this.updateDuration, 5000)
        }
      },
      immediate: true
    },
    "profile.employee_id": {
      handler () {
        if (this.profile.employee_id) {
          this.getActiveEntry()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['setProfile']),
    async saveEntry(data) {
      let method, endpoint
      if (data.id) {
        method = 'PATCH'
        endpoint = endpoints.fetch_timeclock(data.id)
      }
      else {
        method = 'POST'
        endpoint = endpoints.fetch_timeclock()
      }

      if (data.start_time !== undefined) {
        data.start_time = data.start_time.toISOString()
      }
      if (data.end_time !== undefined) {
        data.end_time = data.end_time.toISOString()
      }
      if (data.comment !== undefined) {
        data.comment = data.comment || ""
      }
      if(data.pauses !== undefined) {
        data.pauses = data.pauses.map(p => {
          if (!!p.start_time) {
            p.start_time = p.start_time.toISOString()
          }
          if (!!p.end_time) {
            p.end_time = p.end_time.toISOString()
          }
          return p
        })
      }
      this.loading = true
      try {
        const r = await api(endpoint, data, {method})
        if (r.ok) {
          let newEntry = await r.json()
          if(newEntry.end_time !== null){
            this.value = {
              id: null,
              start_time: null,
              comment: "Arbeitszeit",
              pauses: []
            }
          } else {
            this.value = {
              id: newEntry.id,
              start_time: moment(newEntry.start_time),
              comment: newEntry.comment,
              pauses: newEntry.pauses.map(p => {
                if (!!p.start_time){
                  p.start_time = moment(p.start_time)
                }
                if (!!p.end_time){
                  p.end_time = moment(p.end_time)
                }
                return p
              })
            }
          }
          this.$emit("change", this.value)
        } else {
          // TODO: error handling
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.loading = false
      }
    },
    async getActiveEntry () {
      this.loading = true

      let filters = {
        employee: this.profile.employee_id,
        end_time_null: true
      }

      try {
        const r = await api(endpoints.fetch_timeclock(), filters, {method: 'GET'})
        if (r.ok) {
          const d = await r.json()
          if (d.length > 0){
            this.value = {
              id: d[0].id,
              start_time: moment(d[0].start_time),
              comment: d[0].comment,
              pauses: d[0].pauses.map(p => {
                if (!!p.start_time){
                  p.start_time = moment(p.start_time)
                }
                if (!!p.end_time){
                  p.end_time = moment(p.end_time)
                }
                return p
              })
            }
          }
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.loading = false
      }
    },
    async onStartClick () {
      if (this.timerActive) {
        if (this.timerPaused) {
          this.saveEntry({
            id: this.value.id,
            pauses: this.value.pauses.map(p => {
              if (!p.end_time){
                return {...p, end_time: this.now()}
              }
              return p
            })
          })
        }
        else {
          this.saveEntry({
            id: this.value.id,
            pauses: [...this.value.pauses, {entry: this.value.id, start_time: this.now()}]
          })
        }
      }
      else {
        this.saveEntry({
          start_time: this.now(),
          comment: this.value.comment
        })
      }
    },
    onStopClick () {
      let obj = {
        id: this.value.id,
        end_time: this.now(),
      }
      if (this.timerPaused) {
        obj.pauses = this.value.pauses.map(p => {
          if (!p.end_time){
            return {...p, end_time: this.now()}
          }
          return p
        })
      }
      this.saveEntry(obj)
    },
    onCommentChange () {
      if (this.value.id) {
        this.saveEntry({
          id: this.value.id,
          comment: this.value.comment,
        })
      }
    },
    now () {
      return moment().seconds(0).milliseconds(0)
    },
    updateDuration () {
      if (!this.value.start_time) {
        this.duration = 0
        this.pauseDuration = 0
      }
      else {
        let now = this.now()
        this.pauseDuration = this.value.pauses.reduce((accumulator, item) => {
          let pauseEnd = item.end_time || now
          return accumulator + Math.floor(moment.duration(pauseEnd.diff(item.start_time)).asMinutes())
        }, 0.0)

        let start = moment(this.value.start_time)
        this.duration = Math.floor(moment.duration(now.diff(start)).asMinutes()) - this.pauseDuration
      }
    },
    formatTime: formatTime,
    formatTimeStringDuration: formatTimeStringDuration
  },
}
</script>

<style>
</style>
